import {useDisclosure} from '@mantine/hooks';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {useEffect, useMemo} from 'react';
import {TITLE_SUFFIX} from '../../../../config/constants';
import {GoogleAdBannerName} from '../../../../config/constants/googleAdBanner';
import {GSSKT_HOST} from '../../../../config/constants/hosts';
import {COLUMN_LIMITED_CONDITIONS} from '../../../../core/entities/columns/constants';
import type {PostSchema} from '../../../../core/entities/columns/schema';
import type {GetRecruitmentListResponse} from '../../../../core/entities/recruitment/schema';
import type {GetCompanyInfoResponse} from '../../../../core/repositories/companiesRepository';
import {GoogleAdBanner} from '../../../../shared/components/Banner/GoogleAdBanner';
import {SpFloatContent} from '../../../../shared/components/SpFloatContent';
import {createArticleDetailStructuredData} from '../../../../shared/services/createArticleDetailStructuredDate';
import {useGoogleAdBanner} from '../../../../shared/services/useGoogleAdBanner';
import {isSponsored} from '../../usecases/isSponsored';
import {useHandleColumnClip} from '../../usecases/useColumnClip';
import {RecommendRecruitmentList} from '../Container/RecommendRecruitmentList';
import {PostBody} from './Body';
import {Breadcrumbs} from './Breadcrumbs';
import {CompanyInfo} from './CompanyInfo';
import {Header} from './Header';
import {RecruitingInfoList} from './RecruitingInfoList';
import {TableOfContents} from './TableOfContents';

type Props = {
  parsedContent: string;
  post: PostSchema;
  companyInfo?: GetCompanyInfoResponse;
  recruitmentList?: GetRecruitmentListResponse;
  metaDescription?: string;
  eyeCatchImageURL?: string;
};

export type ColumnClip = {
  isClipped: boolean;
  clipCount: number;
  handleClip: () => Promise<void>;
};

export const ArchivePage: React.FC<Props> = ({
  post,
  parsedContent,
  companyInfo,
  recruitmentList,
  metaDescription,
  eyeCatchImageURL,
}: Props) => {
  const router = useRouter();
  /*
   * ホスト名がlocalhostになっているため、OGPやSEOのURLを指定する要素に使えない.
   * 一旦ベタ書きで対応し、後に修正する.
   * todo URLのベタ書きを環境変数等から取得するように修正する.
   */
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath)}`;
  const googleAdBannerNames = [GoogleAdBannerName.D_FEED_LEFT_SMALL];
  const {setShouldRefetch} = useGoogleAdBanner(googleAdBannerNames);

  const {title} = post;
  const sponsored = useMemo(() => isSponsored(post), [post]);
  const isRequiredLogin =
    (post.limitedConditions &&
      post.limitedConditions.length > 0 &&
      post.limitedConditions.includes(COLUMN_LIMITED_CONDITIONS.LOGIN)) ??
    false;

  const columnClip = useHandleColumnClip({
    columnId: post.id,
    clipCount: post.favoriteCount,
    isClipped: post.favorited,
  });

  const [
    isOpenTableOfContentsModal,
    {open: openTableOfContentsModal, close: closeTableOfContentsModal},
  ] = useDisclosure(false);

  useEffect(() => {
    setShouldRefetch(true);
  }, []);

  return (
    <>
      <Head>
        <title key="title">{title + TITLE_SUFFIX}</title>
        {metaDescription && (
          <>
            <meta
              key="description"
              name="description"
              content={metaDescription}
            />
            <meta
              key="og:description"
              property="og:description"
              content={metaDescription}
            />
            <meta
              key="twitter:description"
              name="twitter:description"
              content={metaDescription}
            />
          </>
        )}
        <meta
          key="og:title"
          property="og:title"
          content={`${title}${TITLE_SUFFIX}`}
        />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta key="og:image" property="og:image" content={post.ogpImageURL} />
        <meta
          key="twitter:title"
          name="twitter:title"
          content={`${title}${TITLE_SUFFIX}`}
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content={post.ogpImageURL}
        />
        <link rel="canonical" href={currentUrl} key="canonical" />
        <script type="application/ld+json">
          {JSON.stringify(
            createArticleDetailStructuredData(post, metaDescription),
          )}
        </script>
        <meta name="robots" content="max-image-preview:large" />
      </Head>
      <Breadcrumbs post={post} />
      <div className="w-full max-w-[960px] mx-auto flex flex-col gap-[16px]">
        <SpFloatContent openModal={openTableOfContentsModal} scrollY={580} />
        {sponsored ? (
          <Header
            isSponsored={true}
            title={title}
            coverImageURL={eyeCatchImageURL}
            tags={post.tags}
            clip={columnClip}
            publishedAt={post.publishDateTime}
            companyName={companyInfo?.name ?? ''}
          />
        ) : (
          <Header
            isSponsored={false}
            title={title}
            coverImageURL={eyeCatchImageURL}
            tags={post.tags}
            clip={columnClip}
            updatedAt={post.updateDateTime}
          />
        )}
        <div className="w-full flex flex-col gap-[8px]">
          <TableOfContents
            className="mx-16 pc:mx-0"
            closeTableOfContentsModal={closeTableOfContentsModal}
            isOpenTableOfContentsModal={isOpenTableOfContentsModal}
          />
          <PostBody
            parsedContent={parsedContent}
            isSponsored={sponsored}
            title={title}
            currentUrl={currentUrl}
            isRequiredLogin={isRequiredLogin}
          />
          <div className="flex flex-col items-center justify-center">
            {!sponsored && (
              <div className="hidden pc:flex justify-center h-[92px] py-16">
                <GoogleAdBanner
                  googleAdBannerName={GoogleAdBannerName.C_UNDER_POST}
                />
              </div>
            )}
          </div>
        </div>
        {sponsored && (
          <>
            {companyInfo && <CompanyInfo data={companyInfo} />}
            {recruitmentList?.recruitments !== undefined &&
              recruitmentList.recruitments.length > 0 && (
                <RecruitingInfoList list={recruitmentList.recruitments} />
              )}
          </>
        )}
        {!sponsored && (
          <RecommendRecruitmentList industryIds={post.industryIDs ?? []} />
        )}
      </div>
    </>
  );
};
